"use client";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { useForm } from "react-hook-form";
import { loginFormSchema, LoginFormValues, LOGIN_ERROR_MESSAGE } from "../model/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/shared/ui/form";
import { userLogin } from "@/server-action/auth/user-login";
import Link from "next/link";
import { Checkbox } from "@/shared/ui/checkbox";
import { useAuthStore } from "@/shared/store/auth";
import { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Loader2 } from "lucide-react";

export function LoginForm({ returnUrl }: { returnUrl?: string }) {
  const t = useTranslations();
  const router = useRouter();
  const { savedEmail, setSavedEmail, clearSavedEmail } = useAuthStore();
  const [shouldSaveEmail, setShouldSaveEmail] = useState(true);
  const [isPending, startTransition] = useTransition();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: savedEmail,
      password: "",
    },
  });

  const onSubmit = (values: LoginFormValues) => {
    startTransition(async () => {
      try {
        const result = await userLogin({ email: values.email, password: values.password });
        if (!result.ok) {
          throw new Error(result.error.message);
        }

        if (shouldSaveEmail) {
          setSavedEmail(values.email);
        } else {
          clearSavedEmail();
        }

        router.push(returnUrl || "/");
      } catch (error) {
        if (error instanceof Error) {
          form.setError("root", {
            message: LOGIN_ERROR_MESSAGE,
          });
        }
      }
    });
  };

  useEffect(() => {
    if (savedEmail) {
      form.setValue("email", savedEmail);
    }
  }, [form, savedEmail]);

  return (
    <Card className="pc:rounded-24 pc:h-[562px] w-full max-w-[1280px] overflow-hidden bg-white">
      <CardContent className="pc:grid-cols-2 pc:grid fle h-full flex-col p-0">
        <div className="pc:h-full tb:h-70 relative h-[126px] overflow-hidden">
          <img
            src="/images/home/login_banner.webp"
            alt="login-banner"
            className="pc:h-full tb:h-70 h-[126px] w-full object-cover"
          />
        </div>

        <Form {...form}>
          <form
            className="pc:px-[108px] tb:pt-21 tb:px-10 pc:pt-12 pc:pb-6 pc:gap-14 flex flex-col px-4 py-10"
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
          >
            <div>
              <div className="flex flex-col">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="">
                      <FormLabel htmlFor="email" className="body-md-bold text-gray-900">
                        {t("f_login_lable_id")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          id="email"
                          type="email"
                          variant="public"
                          placeholder={t("f_login_placeholder_id")}
                          maxLength={50}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="tb:mt-6 mt-3">
                      <FormLabel htmlFor="password" className="body-md-bold text-gray-900">
                        {t("f_login_lable_pw")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          id="password"
                          type="password"
                          variant="public"
                          placeholder={t("f_login_placeholder_pw")}
                          maxLength={50}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {(form.formState.errors.email || form.formState.errors.password || form.formState.errors.root) && (
                  <span className="body-sm-regular tb:mt-2 mt-[6px] whitespace-pre-line text-red-500">
                    {t("f_login_err_id_1") + "\n" + t("f_login_err_id_2")}
                  </span>
                )}

                <Button type="submit" className="mt-10 w-full" variant="primary" size="lg" disabled={isPending}>
                  {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("f_login_btn_login")}
                </Button>
              </div>

              <div className="tb:mt-4 tb:gap-[27px] mt-3 flex flex-col items-center gap-8">
                <div className="tb:gap-2 flex w-full items-center gap-[6px]">
                  <Checkbox
                    id="email-save"
                    variant="public"
                    checked={shouldSaveEmail}
                    onCheckedChange={(checked) => setShouldSaveEmail(checked === true)}
                  />
                  <FormLabel htmlFor="email-save" className="body-md-regular cursor-pointer text-gray-900">
                    {t("f_login_save")}
                  </FormLabel>
                </div>

                <div className="tb:gap-[14px] flex items-center gap-[10px]">
                  <Link href="/signup" className="body-sm-medium text-gray-500">
                    {t("login_btn_jm")}
                  </Link>
                  <span className="h-[10px] w-[1px] bg-gray-200" />
                  <Link href="/password-reset" className="body-sm-medium text-gray-500">
                    {t("f_login_btn_pw")}
                  </Link>
                </div>
              </div>
            </div>

            <div className="pc:mt-0 tb:mt-15 mt-30 text-center">
              <span className="body-sm-regular text-gray-700">
                {t("f_login_text_1")}{" "}
                <Link href="/inquiry" className="body-sm-bold text-primary-600 underline">
                  {t("f_login_text_2")}
                </Link>
                {t("f_login_text_3")}
              </span>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
